/**
 * Coercion of an agent-supplied `request_input` schema onto the flow contract.
 *
 * `request_input` used to cast whatever the agent passed straight into
 * `InputSchema`, so a choice gate could be persisted with option ids no host
 * could render — a run parked forever on a question no human could answer.
 * This module tolerates the shapes agents actually reach for (a bare string per
 * option, an option with a label and no id) and refuses the rest, because a
 * refusal the agent can read and retry beats a stored gate nobody can answer.
 *
 * Exported because hosts normalize the same payloads when they read a stored
 * gate; one shared rule is what keeps the write and read sides in agreement.
 */

import type { InputSchema } from "@skaile/workspaces/types";
import type * as z from "zod";
import { InputSchema as InputSchemaContract } from "./schema.js";

/**
 * The shapes `request_input` accepts, phrased for an agent retrying a refused
 * call. Every optional key the contract admits is listed, because validation is
 * strict: an agent refused for one unknown key would otherwise read a key it
 * omitted here as illegal too.
 */
export const INPUT_SCHEMA_SHAPES_HINT =
  "{ kind: 'text', multiline? }, { kind: 'choice', options: [{ id, label, description? }], multiple? }, { kind: 'form', fields: [{ id, label, type: text|textarea|number|choice|file|date|boolean, required?, options?, accept? }] }, or { kind: 'file', accept?, maxSize?, multiple? }";

/**
 * Outcome of {@link coerceInputSchema}: either a schema in the contract shape,
 * or the reasons it was refused — one entry per problem, each naming its path,
 * closed by a final entry naming the accepted shapes. The hint travels with the
 * issues so every caller's refusal is actionable without re-appending it.
 */
export type InputSchemaCoercion =
  | { ok: true; schema: InputSchema }
  | { ok: false; issues: string[] };

const KNOWN_KINDS = ["text", "choice", "form", "file"] as const;

/** A coerced option or form field, and whether its id was derived from its label. */
type Prepared = { entry: Record<string, unknown>; derivedId: boolean };

/**
 * Brings an untrusted `request_input` schema into the `InputSchema` contract.
 *
 * Tolerated: a JSON string wrapper, a choice option given as a bare string, and
 * an option or form field whose id is missing but whose label can supply one.
 * Refused: an unknown `kind`, an option or field that carries no usable label, a
 * duplicate explicit id, an id or label of the wrong type, and a choice or form
 * with nothing to answer — anything a host could not turn into an answerable
 * control. Options are never dropped: every unusable one produces an issue.
 *
 * @param value Raw `schema` argument as the agent supplied it.
 * @returns The contract-shaped schema, or the issues that make it unanswerable.
 */
export function coerceInputSchema(value: unknown): InputSchemaCoercion {
  let candidate: unknown = value;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      // A free-text box is still answerable, so an unparseable string keeps the
      // fallback it has always had rather than failing the agent's turn.
      return { ok: true, schema: { kind: "text" } };
    }
  }
  if (candidate === null || typeof candidate !== "object") {
    return { ok: true, schema: { kind: "text" } };
  }
  if (Array.isArray(candidate)) {
    return refuse([
      "schema: expected an object with a `kind`, got an array — a bare option list needs { kind: 'choice', options: [...] }",
    ]);
  }

  const raw = candidate as Record<string, unknown>;
  const kind = raw.kind;
  // An unknown kind is refused rather than degraded to text: text would discard
  // the agent's intent and hand the user a box the flow cannot consume.
  if (typeof kind !== "string" || !KNOWN_KINDS.some((known) => known === kind)) {
    return refuse([
      `schema.kind: expected one of ${KNOWN_KINDS.join(", ")}, got ${describe(kind)}`,
    ]);
  }

  const issues: string[] = [];
  const prepared =
    kind === "choice"
      ? prepareChoice(raw, issues)
      : kind === "form"
        ? prepareForm(raw, issues)
        : raw;
  if (issues.length > 0) return refuse(issues);

  const parsed = InputSchemaContract.safeParse(prepared);
  if (!parsed.success) return refuse(formatIssues(parsed.error.issues));
  return { ok: true, schema: parsed.data };
}

/** Closes a refusal with the accepted shapes, so no caller has to know to add them. */
function refuse(issues: string[]): InputSchemaCoercion {
  return { ok: false, issues: [...issues, `valid shapes: ${INPUT_SCHEMA_SHAPES_HINT}`] };
}

function prepareChoice(raw: Record<string, unknown>, issues: string[]): Record<string, unknown> {
  const options = raw.options;
  if (!Array.isArray(options)) {
    issues.push(`options: expected an array of choices, got ${describe(options)}`);
    return raw;
  }
  if (options.length === 0) {
    issues.push(
      "options: expected at least one choice — a choice gate with none cannot be answered",
    );
    return raw;
  }
  return { ...raw, options: coerceChoices(options, "options", issues) };
}

function prepareForm(raw: Record<string, unknown>, issues: string[]): Record<string, unknown> {
  const fields = raw.fields;
  if (!Array.isArray(fields)) {
    issues.push(`fields: expected an array of form fields, got ${describe(fields)}`);
    return raw;
  }
  // A form with no fields is as unanswerable as a choice with no options.
  if (fields.length === 0) {
    issues.push("fields: expected at least one field — a form gate with none cannot be answered");
    return raw;
  }
  const prepared = fields.map((field, index) => coerceField(field, `fields[${index}]`, issues));
  return { ...raw, fields: settleIds(prepared, "fields", issues) };
}

function coerceChoices(
  options: unknown[],
  path: string,
  issues: string[],
): Record<string, unknown>[] {
  const prepared = options.map((option, index) =>
    coerceChoice(option, `${path}[${index}]`, issues),
  );
  return settleIds(prepared, path, issues);
}

function coerceChoice(option: unknown, at: string, issues: string[]): Prepared | undefined {
  if (typeof option === "string") {
    const label = option.trim();
    if (label === "") {
      issues.push(`${at}: expected a non-empty string`);
      return undefined;
    }
    return { entry: { id: label, label }, derivedId: true };
  }
  if (!isPlainObject(option)) {
    issues.push(
      `${at}: expected a string or an object with { id, label }, got ${describe(option)}`,
    );
    return undefined;
  }
  const identity = coerceIdentity(option, at, issues);
  if (!identity) return undefined;
  const entry: Record<string, unknown> = { ...option, id: identity.id, label: identity.label };
  const { description } = entry;
  // An absent, null, or blank description carries nothing; the contract admits
  // only a non-empty string, and anything else stays for it to reject.
  if (
    description === null ||
    description === undefined ||
    (typeof description === "string" && description.trim() === "")
  ) {
    delete entry.description;
  }
  return { entry, derivedId: identity.derivedId };
}

function coerceField(field: unknown, at: string, issues: string[]): Prepared | undefined {
  if (!isPlainObject(field)) {
    issues.push(`${at}: expected an object with { id, label, type }, got ${describe(field)}`);
    return undefined;
  }
  const identity = coerceIdentity(field, at, issues);
  if (!identity) return undefined;
  const entry: Record<string, unknown> = { ...field, id: identity.id, label: identity.label };
  const options = field.options;
  // A select with nothing to select is the same unrenderable control a choice
  // gate with no options is, one level down.
  if (field.type === "choice" && (!Array.isArray(options) || options.length === 0)) {
    issues.push(
      `${at}.options: expected at least one choice — a choice field with none cannot be answered`,
    );
    return undefined;
  }
  if (Array.isArray(options)) {
    entry.options = coerceChoices(options, `${at}.options`, issues);
  }
  return { entry, derivedId: identity.derivedId };
}

/**
 * Resolves the `id`/`label` pair of an option or field, letting either supply
 * the other. An id or label of the wrong type is refused rather than replaced:
 * silently swapping in a different id is the failure this module exists to stop
 * — an agent that branches on the id it wrote would never see the substitution.
 */
function coerceIdentity(
  raw: Record<string, unknown>,
  at: string,
  issues: string[],
): { id: string; label: string; derivedId: boolean } | undefined {
  const id = explicitText(raw.id, at, "id", issues);
  const label = explicitText(raw.label, at, "label", issues);
  if (!id.ok || !label.ok) return undefined;
  const text = label.value ?? id.value;
  if (text === undefined) {
    issues.push(`${at}: expected a non-empty \`label\`, got ${describe(raw.label)}`);
    return undefined;
  }
  return { id: id.value ?? text, label: text, derivedId: id.value === undefined };
}

/**
 * An explicit `id`/`label`, or `undefined` when the agent supplied nothing to
 * carry (absent, null, or blank). Any other type is intent in the wrong shape,
 * so it is reported instead of dropped.
 */
function explicitText(
  value: unknown,
  at: string,
  key: "id" | "label",
  issues: string[],
): { ok: true; value: string | undefined } | { ok: false } {
  if (value === undefined || value === null) return { ok: true, value: undefined };
  if (typeof value !== "string") {
    issues.push(`${at}.${key}: expected a non-empty string, got ${describe(value)}`);
    return { ok: false };
  }
  const trimmed = value.trim();
  return { ok: true, value: trimmed === "" ? undefined : trimmed };
}

/**
 * Settles ids across a list of options or fields. Every explicit id is reserved
 * before any derived one is filled in, so a collision's verdict cannot depend on
 * which side of it came first — and a refusal never names an id the agent did
 * not write.
 */
function settleIds(
  prepared: (Prepared | undefined)[],
  path: string,
  issues: string[],
): Record<string, unknown>[] {
  const used = new Set<string>();
  const reserved = prepared.map((item, index) => {
    if (!item || item.derivedId) return item;
    const id = String(item.entry.id);
    if (used.has(id)) {
      issues.push(
        `${path}[${index}]: duplicate id "${id}" — ids must be unique or a host cannot tell the answers apart`,
      );
      return undefined;
    }
    used.add(id);
    return item;
  });

  const settled: Record<string, unknown>[] = [];
  reserved.forEach((item, index) => {
    if (!item) return;
    if (item.derivedId) item.entry.id = uniqueId(String(item.entry.id), index, used);
    used.add(String(item.entry.id));
    settled.push(item.entry);
  });
  return settled;
}

function uniqueId(base: string, index: number, used: Set<string>): string {
  if (!used.has(base)) return base;
  // Two options can legitimately share a label; the index disambiguates the id
  // derived from it so the answer stays unambiguous.
  let attempt = index;
  let candidate = `${base}-${attempt}`;
  while (used.has(candidate)) {
    attempt += 1;
    candidate = `${base}-${attempt}`;
  }
  return candidate;
}

function formatIssues(issues: readonly z.core.$ZodIssue[]): string[] {
  return issues.map((issue) => {
    const path = issue.path.reduce<string>((acc, segment) => {
      if (typeof segment === "number") return `${acc}[${segment}]`;
      return acc === "" ? String(segment) : `${acc}.${String(segment)}`;
    }, "");
    return `${path === "" ? "schema" : path}: ${issue.message}`;
  });
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === undefined) return "nothing";
  if (value === null) return "null";
  if (Array.isArray(value)) return "an array";
  if (typeof value === "string") return `the string ${JSON.stringify(value)}`;
  if (typeof value === "object") return "an object";
  return `${typeof value} ${String(value)}`;
}
